import React, { useState, useEffect } from "react";
import { auth, db, storage } from "./firebase";
import { doc, setDoc, onSnapshot, updateDoc, deleteDoc, addDoc, collection } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { signOut } from "firebase/auth";
import ResumeDashboard from "./ResumeDashboard";
import { calculateQualityScore, getTimePattern, sumDailyStats, getDateRange } from "./services/AnalyticsEngine";

// ─── CONFIG ──────────────────────────────────────────────────
const WORKER_URL = "https://supermedia-newsletter.procreaction.workers.dev";

// ─── HILFSFUNKTIONEN ─────────────────────────────────────────
const getWeekNumber = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - (d.getDay() + 6) % 7);
  const week1 = new Date(d.getFullYear(), 0, 4);
  return 1 + Math.round(((d.getTime() - week1.getTime()) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
};

export default function ProfilePage({ 
  user, isAdmin, isArtist, pendingUsers = [],
  allUsers = [], tracks = [], showToast, shareInvite, 
  updateUserRole, onLoginClick, onJoinClick, approveUser, 
  rejectUser, deleteUser, openTerms, profileData = {}   
}) {
  const [profile, setProfile] = useState({ 
    name: "", vision: "", bio: "", avatar: "", cover: "" 
  });
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState({ type: null }); 
  const [isUserListExpanded, setIsUserListExpanded] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [messageTarget, setMessageTarget] = useState(null);
  const [messageText, setMessageText] = useState("");
  const [notifications, setNotifications] = useState([]);
  const [showMessageComposer, setShowMessageComposer] = useState(false);
  
  const [composerText, setComposerText] = useState("");
  const adminUser = allUsers.find(u => u.isAdmin);

  const [sendingWeeklyReport, setSendingWeeklyReport] = useState(false);
  const [weeklyReportSent, setWeeklyReportSent] = useState(false);
  const [newFeaturesText, setNewFeaturesText] = useState("");

  // ✅ NEU: Artist Track Stats
  const myTracks = tracks?.filter(t => t.userId === user?.uid) || [];
  const totalPlays = myTracks.reduce((sum, t) => sum + (t.plays || 0), 0);
  const totalLikes = myTracks.reduce((sum, t) => sum + (t.likes?.length || 0), 0);
  const totalDislikes = myTracks.reduce((sum, t) => sum + (t.dislikes?.length || 0), 0);
  const totalSkips = myTracks.reduce((sum, t) => sum + (t.skipCount || 0), 0);
  const [isStatsOpen, setIsStatsOpen] = useState(false);
  const [isStudioCheckOpen, setIsStudioCheckOpen] = useState(false);
  const [studioCheckLoading, setStudioCheckLoading] = useState(false);
  const [studioCheckText, setStudioCheckText] = useState("");

  const handleSendToAdmin = async () => {
    if (!composerText.trim()) return;
    const adminUsers = allUsers.filter(u => u.isAdmin); // ✅ an alle Admins
    if (adminUsers.length === 0) return;

    try {
      // ✅ An alle Admins senden
      await Promise.all(adminUsers.map(admin => 
        addDoc(collection(db, "notifications", admin.id, "messages"), {
          text: composerText,
          from: user.uid,
          fromName: profile.name || user.email,
          read: false,
          createdAt: new Date()
        })
      ));
      setComposerText("");
      setShowMessageComposer(false);
      showToast("Nachricht gesendet! ✅");
    } catch (err) {
      console.error("Send error:", err);
      showToast("Senden fehlgeschlagen", "error");
    }
  };

  const generateStudioCheck = async () => {
    if (studioCheckLoading || studioCheckText) return;
    setStudioCheckLoading(true);
    
    try {
      // Track Stats zusammenstellen
      const trackSummary = myTracks.map(t => ({
        title: t.title,
        plays: t.plays || 0,
        likes: t.likes?.length || 0,
        dislikes: t.dislikes?.length || 0,
        skips: t.skipCount || 0,
        completions: t.completions || 0,
        onAir: t.onAir,
        completionRate: t.plays > 0 ? Math.round(((t.completions || 0) / t.plays) * 100) : 0,
      }));
  
      const prompt = `Du bist ein erfahrener A&R Coach und Music Consultant für ein Community Radio Collective namens "streetwise RADIO".
      
  Analysiere die Performance-Daten dieses Artists und gib ehrliches, direktes Feedback auf Deutsch. Sei konkret, motivierend aber auch ehrlich – keine leeren Floskeln.
  
  ARTIST: ${profile.name || "Unknown Artist"}
  TRACKS:
  ${trackSummary.map(t => `- "${t.title}": ${t.plays} Plays, ${t.likes} Likes, ${t.skips} Skips, ${t.completionRate}% Completion Rate, ${t.onAir ? "ON AIR" : "OFF AIR"}`).join("\n")}
  
  GESAMT: ${totalPlays} Plays, ${totalLikes} Likes, ${totalDislikes} Dislikes, ${totalSkips} Skips
  
  Gib Feedback in diesem Format:
  🎯 DEIN STATUS
  [1-2 Sätze über die aktuelle Performance]
  
  🔥 WAS FUNKTIONIERT
  [Konkret was gut läuft]
  
  ⚠️ WAS ZU TUN IST
  [Konkrete Empfehlungen]
  
  💡 HOT TAKE
  [Eine ehrliche, direkte Einschätzung]`;
  
      const response = await fetch("https://supermedia-newsletter.procreaction.workers.dev/claude", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-5",
          max_tokens: 1000,
          messages: [{ role: "user", content: prompt }]
        })
      });
  
      const data = await response.json();
      const text = data.content?.[0]?.text || "Keine Antwort erhalten.";
      setStudioCheckText(text);
  
    } catch(e) {
      setStudioCheckText("Fehler beim Laden. Bitte nochmal versuchen.");
      console.error("Studio Check Error:", e);
    } finally {
      setStudioCheckLoading(false);
    }
  };

  // ─── WEEKLY ARTIST REPORT ──────────────────────────────────
  const handleSendWeeklyReport = async (specificArtist = null) => {
    const targetArtists = specificArtist 
      ? [specificArtist] 
      : allUsers.filter(u => (u.isArtist || u.isAdmin) && u.email);

    if (targetArtists.length === 0) {
      showToast?.("Keine Artists mit Email gefunden.", "error");
      return;
    }

    setSendingWeeklyReport(true);
    setWeeklyReportSent(false);
    let sentCount = 0;

    const safeTracks = Array.isArray(tracks) ? tracks : [];
    const currentDates = getDateRange(7);
    const kw = getWeekNumber(new Date());

    // Globale Stats (bleiben gleich für alle)
    const globalPlays = safeTracks.reduce((s, t) => s + sumDailyStats(t, currentDates, 'plays'), 0);
    const globalCompletions = safeTracks.reduce((s, t) => s + sumDailyStats(t, currentDates, 'completions'), 0);
    const globalLikes = safeTracks.reduce((s, t) => s + sumDailyStats(t, currentDates, 'likes'), 0);
    const globalListeners = allUsers.filter(u => (u.lastLogin?.seconds * 1000) > Date.now() - 7 * 24 * 60 * 60 * 1000).length;
    const globalCompletionRate = globalPlays > 0 ? Math.round((globalCompletions / globalPlays) * 100) : 0;

    const hourTotals = {};
    safeTracks.forEach(t => {
      if (!t.playsByHour) return;
      Object.entries(t.playsByHour).forEach(([h, c]) => { hourTotals[h] = (hourTotals[h] || 0) + c; });
    });
    const peakHour = Object.entries(hourTotals).sort((a, b) => b[1] - a[1])[0];

    for (const artist of targetArtists) {
      try {
        // Filterung der Tracks des Artists für den Zeitraum
        const myTracks = safeTracks
          .filter(t => t.userId === artist.id)
          .map(t => ({
            ...t,
            periodPlays: sumDailyStats(t, currentDates, 'plays'),
            periodCompletions: sumDailyStats(t, currentDates, 'completions'),
            qScore: calculateQualityScore(t, currentDates),
            timePattern: getTimePattern(t),
          }))
          .sort((a, b) => b.qScore - a.qScore)
          .slice(0, 5);

        // KORREKTUR: Nur Plays der Woche zählen
        const totalArtistPlaysWeek = myTracks.reduce((s, t) => s + (t.periodPlays || 0), 0);
        const totalArtistLikesWeek = myTracks.reduce((s, t) => s + (t.likes?.length || 0), 0);

        let hotTake = "";
        if (myTracks.length > 0) {
          try {
            const prompt = `Du bist der freundliche Host von streetwise RADIO. 
Schreibe einen kurzen, positiven Hot Take (2-3 Sätze auf Deutsch) für "${artist.name}".
Beobachte den Vibe, nicht die Verkaufszahlen. Erwähne "${myTracks[0].title}". 
Formuliere es als Inspiration: "Es wäre spannend, mehr in diesem Stil zu hören" statt "Du musst...". 
Kein "WAS ZU TUN IST". Keine Tabellen-Begriffe wie "Completion Rate".`;

            const aiRes = await fetch(`${WORKER_URL}/claude`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                model: "claude-sonnet-5",
                max_tokens: 300,
                messages: [{ role: "user", content: prompt }]
              })
            });
            const aiData = await aiRes.json();
            hotTake = aiData.content?.[0]?.text || "";
          } catch(e) { console.warn("AI failed for", artist.name); }
        }

        const html = `<!DOCTYPE html>
<html>
<body style="background:#0a0a0a;color:#fff;font-family:'Helvetica Neue',sans-serif;padding:0;margin:0">
  <div style="max-width:600px;margin:0 auto;padding:30px 20px">
    <div style="border-bottom:1px solid #1a1a1a;padding-bottom:20px;margin-bottom:25px">
      <p style="color:#00acc1;font-size:0.7rem;letter-spacing:4px;margin:0 0 8px">📻 STREETWISE RADIO</p>
      <h1 style="color:#fff;font-size:1.4rem;font-weight:900;margin:0;letter-spacing:2px">Weekly Resumé</h1>
      <p style="color:#555;font-size:0.8rem;margin:6px 0 0">KW ${kw} – Letzte 7 Tage</p>
    </div>

    <p style="color:#ccc;font-size:1rem;line-height:1.6;margin-bottom:25px">
      Hey ${artist.name || "Artist"}! Hier kommt dein wöchentliches Resumé zu deinen Tracks im streetwise Radio.
    </p>

    ${myTracks.length > 0 ? `
    <div style="margin-bottom:25px">
      <h2 style="color:#00acc1;font-size:0.7rem;letter-spacing:3px;margin:0 0 12px">DEINE TOP ${Math.min(5, myTracks.length)} TRACKS DIESE WOCHE</h2>
      ${myTracks.map((t, i) => `
      <div style="display:flex;justify-content:space-between;align-items:center;padding:10px 0;border-bottom:1px solid #1a1a1a">
        <div>
          <span style="color:#555;font-size:0.75rem">${i+1}. </span>
          <strong style="color:#fff;font-size:0.9rem">${t.title}</strong>
          ${t.timePattern ? `<span style="color:${t.timePattern.color};font-size:0.7rem;margin-left:8px">${t.timePattern.label}</span>` : ""}
          <br>
          <span style="color:#555;font-size:0.75rem">${t.periodPlays} Plays · ${t.periodCompletions} Completions</span>
        </div>
        <span style="color:#00acc1;font-weight:900;font-size:0.8rem">Q${t.qScore}</span>
      </div>`).join('')}
    </div>
    ` : `
    <div style="background:#111;padding:15px;border-radius:8px;margin-bottom:25px;border-left:3px solid #333">
      <p style="color:#555;margin:0;font-size:0.85rem">Diese Woche noch keine Track-Aktivität. Lade bald neue Tracks hoch! 🎵</p>
    </div>
    `}

    ${hotTake ? `
    <div style="background:#111;padding:20px;border-radius:10px;margin-bottom:25px;border-left:3px solid #00acc1">
      <p style="color:#00acc1;font-size:0.65rem;letter-spacing:3px;margin:0 0 10px;font-weight:900">🔥 HOT TAKE</p>
      <p style="color:#ddd;font-size:0.9rem;line-height:1.7;margin:0;white-space:pre-wrap">${hotTake}</p>
    </div>
    ` : ""}

    ${newFeaturesText ? `
    <div style="margin-bottom:25px">
      <h2 style="color:#00acc1;font-size:0.7rem;letter-spacing:3px;margin:0 0 12px">✨ NEUE FEATURES IN DER APP</h2>
      <p style="color:#ccc;font-size:0.85rem;line-height:1.6;margin:0">${newFeaturesText.replace(/\n/g, '<br>')}</p>
    </div>
    ` : ""}

    <div style="margin-bottom:25px">
      <h2 style="color:#00acc1;font-size:0.7rem;letter-spacing:3px;margin:0 0 12px">📊 RADIO STATS DIESE WOCHE</h2>
      <table style="width:100%;border-collapse:collapse">
        <tr><td style="padding:6px 0;color:#666;font-size:0.8rem">Total Plays</td><td style="padding:6px 0;color:#fff;font-weight:bold;text-align:right">${globalPlays}</td></tr>
        <tr><td style="padding:6px 0;color:#666;font-size:0.8rem">Completion Rate</td><td style="padding:6px 0;color:#fff;font-weight:bold;text-align:right">${globalCompletionRate}%</td></tr>
        <tr><td style="padding:6px 0;color:#666;font-size:0.8rem">Aktive Listeners</td><td style="padding:6px 0;color:#fff;font-weight:bold;text-align:right">${globalListeners}</td></tr>
        ${peakHour ? `<tr><td style="padding:6px 0;color:#666;font-size:0.8rem">Peak Hour</td><td style="padding:6px 0;color:#fff;font-weight:bold;text-align:right">${peakHour[0]}:00 Uhr</td></tr>` : ""}
      </table>
    </div>

    <div style="background:#111;padding:20px;border-radius:10px;margin-bottom:25px;border-left:3px solid #555">
      <p style="color:#00acc1;font-size:0.65rem;letter-spacing:3px;margin:0 0 8px;font-weight:900">💡 DEINE IDEEN ZÄHLEN</p>
      <p style="color:#ccc;font-size:0.85rem;line-height:1.6;margin:0">
        Hast du Ideen oder Feedback? Antworte einfach auf diese Email.<br>
        Ich freue mich bald wieder einen neuen Track von dir zu hören.
      </p>
    </div>

    <div style="text-align:center;margin:30px 0 40px">
      <a href="https://radio.streetwise.studio" 
         style="background:#00acc1;color:#000;padding:15px 30px;border-radius:12px;text-decoration:none;font-weight:900;font-size:0.9rem;display:inline-block;letter-spacing:1px">
        JETZT REINHÖREN & TRACKS CHECKEN
      </a>
      <p style="color:#555;font-size:0.7rem;margin-top:12px">radio.streetwise.studio</p>
    </div>

    <div style="border-top:1px solid #1a1a1a;padding-top:20px">
      <p style="color:#ccc;font-size:0.85rem;margin:0">Greetings</p>
      <p style="color:#fff;font-weight:900;margin:4px 0">Mat</p>
      <p style="color:#555;font-size:0.8rem;margin:0">streetwise.STUDIO</p>
    </div>
  </div>
</body>
</html>`;

        await fetch(`${WORKER_URL}/send`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            subject: `📻 streetwise RADIO – Weekly Resumé KW ${kw}`,
            html,
            contacts: [{ email: artist.email, name: artist.name || "Artist" }]
          })
        });
        sentCount++;
        
        // Professionelles Rate-Limiting
        if (targetArtists.length > 1) await new Promise(r => setTimeout(r, 300));

      } catch(e) { console.error("Report failed for", artist.name, e); }
    }

    setSendingWeeklyReport(false);
    setWeeklyReportSent(true);
    showToast?.(`${sentCount} Report(s) erfolgreich versendet! ✅`);
  };



  useEffect(() => {
    if (!user) return;
    const unsub = onSnapshot(doc(db, "users", user.uid), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setProfile({
          name: data.name || "",
          vision: data.vision || "",
          bio: data.bio || "",
          avatar: data.avatar || "",
          cover: data.cover || ""
        });
      } else {
        setIsEditing(true);
      }
    }, (err) => console.error("Profil-Laden Fehler:", err));
    return () => unsub();
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const unsub = onSnapshot(
      collection(db, "notifications", user.uid, "messages"),
      (snap) => {
        console.log("Notifications snap:", snap.docs.length, snap.docs.map(d => d.data())); // ✅ NEU

        const msgs = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
        setNotifications(msgs);
      },
      (err) => console.error("Notifications Fehler:", err) // ✅ NEU: Fehler loggen

    );
    return () => unsub();
  }, [user]);

  const isPending = profileData?.role === "pending";
  const isListener = profileData?.role === "listener";
  const hasNoName = !profile.name || profile.name === "";

  const handleFileUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    const maxSize = type === 'cover' ? 10 * 1024 * 1024 : 5 * 1024 * 1024;
    if (file.size > maxSize) {
      return showToast(`Datei zu groß! Max ${type === 'cover' ? '10MB' : '5MB'}`, "error");
    }
    setUploading({ type });
    try {
      const fileExtension = file.name.split('.').pop();
      const storageRef = ref(storage, `${type}s/${user.uid}/profile.${fileExtension}`);
      const metadata = { contentType: file.type };
      await uploadBytes(storageRef, file, metadata);
      const url = await getDownloadURL(storageRef);
      await updateDoc(doc(db, "users", user.uid), { [type]: url });
      showToast(`${type === 'avatar' ? 'Profilbild' : 'Cover'} aktualisiert!`);
    } catch (err) {
      showToast(`Upload fehlgeschlagen: ${err.code || 'Fehler'}`, "error");
    } finally {
      setUploading({ type: null });
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await setDoc(doc(db, "users", user.uid), {
        ...profile,
        updatedAt: new Date().toISOString(),
      }, { merge: true });
      setIsEditing(false);
      showToast("Profil gespeichert! ✅");
    } catch (err) {
      showToast("Speichern fehlgeschlagen", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!messageText.trim()) return;
    try {
      await addDoc(collection(db, "notifications", messageTarget.id, "messages"), {
        text: messageText,
        from: user.uid,
        fromName: profile.name || "Admin",
        read: false,
        createdAt: new Date()
      });
      setMessageText("");
      setMessageTarget(null);
      showToast("Message gesendet! ✅");
    } catch (err) {
      showToast("Senden fehlgeschlagen", "error");
    }
  };
  

  if (!user) {
    return (
      <div style={{ textAlign: "center", padding: "5rem 2rem", color: "white" }}>
        <div style={{ fontSize: "3rem", marginBottom: "20px" }}>👤</div>
        <h2 style={{ fontSize: "1.2rem", letterSpacing: "2px", fontWeight: "900", marginBottom: "10px" }}>MEMBERS ONLY</h2>
        <p style={{ color: "#666", fontSize: "0.8rem", marginBottom: "30px" }}>Logge dich ein, um dein Profil zu bearbeiten.</p>
        <button onClick={onLoginClick} style={{ padding: "12px 30px", background: "#00acc1", color: "#000", border: "none", borderRadius: "30px", fontWeight: "900", cursor: "pointer", marginBottom: "12px" }}>
          LOGIN
        </button>
        <br />
        <button onClick={onJoinClick} style={{ padding: "12px 30px", background: "none", color: "#666", border: "1px solid #333", borderRadius: "30px", fontWeight: "900", cursor: "pointer", fontSize: "0.75rem" }}>
          NOCH KEIN ACCOUNT? JOIN COLLECTIVE
        </button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: "600px", margin: "0 auto", color: "white", paddingBottom: "50px" }}>

      {/* PENDING MESSAGE */}
      {isPending && (
        <div style={{ margin: "20px", padding: "22px", background: "linear-gradient(135deg, rgba(0,172,193,0.12), rgba(0,172,193,0.05))", border: "1px solid #00acc1", borderRadius: "16px" }}>
          <div style={{ fontSize: "1.5rem", marginBottom: "10px" }}>🎙️</div>
          <h3 style={{ color: "#00acc1", fontSize: "0.9rem", fontWeight: "900", marginTop: 0 }}>DANKE FÜR DEINE ANMELDUNG!</h3>
          <p style={{ fontSize: "0.75rem", color: "#ccc", lineHeight: "1.6", marginBottom: "15px" }}>
            Dein Profil wird aktuell von einem Admin geprüft. 
            Wir bitten um etwas Geduld – sobald du freigeschaltet bist, 
            kannst du eigene MP3 Tracks ins Collective uploaden.
          </p>
          {hasNoName && (
            <>
              <div style={{ width: "100%", height: "1px", background: "#1a2a2a", margin: "15px 0" }} />
              <p style={{ fontSize: "0.7rem", color: "#888", marginBottom: "10px" }}>Nutze die Zeit und richte schon dein Profil ein:</p>
              <ul style={{ fontSize: "0.7rem", color: "#666", paddingLeft: "15px", margin: "0 0 15px 0", lineHeight: "1.8" }}>
                <li>Artist Name festlegen</li>
                <li>Profilbild & Coverbild hochladen</li>
                <li>Kurze Bio schreiben</li>
              </ul>
              <button onClick={() => setIsEditing(true)} style={{ padding: "10px 20px", background: "#00acc1", color: "#000", border: "none", borderRadius: "20px", fontWeight: "900", cursor: "pointer", fontSize: "0.7rem" }}>
                PROFIL JETZT EINRICHTEN →
              </button>
            </>
          )}
        </div>
      )}

      {isPending && !hasNoName && (
        <div style={{ margin: "20px", padding: "15px", background: "rgba(255, 152, 0, 0.1)", border: "1px solid #ff9800", borderRadius: "15px", textAlign: "center" }}>
          <p style={{ fontSize: "0.75rem", color: "#ff9800", fontWeight: "bold", margin: 0 }}>Warteliste: Dein Profil wird aktuell vom Admin geprüft. ⏳</p>
        </div>
      )}

      {isListener && (
        <div style={{ margin: "20px", padding: "20px", background: "rgba(0, 172, 193, 0.05)", border: "1px dashed #00acc1", borderRadius: "15px", textAlign: "center" }}>
          <h4 style={{ color: "#00acc1", fontSize: "0.8rem", marginBottom: "10px" }}>ARTIST WERDEN?</h4>
          <p style={{ fontSize: "0.7rem", color: "#aaa", marginBottom: "15px" }}>Möchtest du eigene Tracks im Radio präsentieren? Bewirb dich als Artist!</p>
          <button onClick={openTerms} style={{ padding: "10px 20px", background: "#00acc1", color: "#000", border: "none", borderRadius: "20px", fontWeight: "900", cursor: "pointer", fontSize: "0.65rem" }}>
            JETZT UPGRADEN
          </button>
        </div>
      )}

      {/* COVER */}
      <div style={{ width: "100%", height: "180px", background: profile.cover ? `url(${profile.cover}) center/cover` : "#111", borderRadius: "0 0 20px 20px", position: "relative", border: "1px solid #222", overflow: "hidden" }}>
        {isEditing && (
          <label style={{ position: "absolute", bottom: "10px", right: "10px", background: "rgba(0,0,0,0.7)", padding: "5px 12px", borderRadius: "10px", fontSize: "0.6rem", cursor: "pointer", border: "1px solid #00acc1" }}>
            {uploading.type === 'cover' ? "UPLOADING..." : "CHANGE COVER"}
            <input type="file" hidden accept="image/*" onChange={(e) => handleFileUpload(e, 'cover')} />
          </label>
        )}
      </div>

      {/* AVATAR + PROFIL */}
      <div style={{ textAlign: "center", marginTop: "-60px", padding: "0 20px" }}>
        <div style={{ width: "110px", height: "110px", borderRadius: "50%", margin: "0 auto 15px", background: profile.avatar ? `url(${profile.avatar}) center/cover` : "#000", border: "4px solid #000", boxShadow: "0 0 15px rgba(0,0,0,0.5)", position: "relative" }}>
          {isEditing && (
            <label style={{ position: "absolute", bottom: "0", right: "0", background: "#00acc1", width: "30px", height: "30px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: "1rem" }}>
              📸 <input type="file" hidden accept="image/*" onChange={(e) => handleFileUpload(e, 'avatar')} />
            </label>
          )}
        </div>

        {/* ✅ NEU: Kein Avatar – Reminder */}
        {!profile.avatar && !isEditing && (
          <div style={{ 
            margin: "10px auto", padding: "10px 16px", 
            background: "rgba(255,152,0,0.08)", border: "1px dashed #ff9800", 
            borderRadius: "12px", maxWidth: "280px"
          }}>
            <p style={{ fontSize: "0.65rem", color: "#ff9800", margin: "0 0 8px", textAlign: "center" }}>
              📸 Lade ein Profilbild hoch um im Artist-Filter sichtbar zu sein!
            </p>
            <button 
              onClick={() => setIsEditing(true)}
              style={{ width: "100%", background: "none", border: "1px solid #ff9800", color: "#ff9800", borderRadius: "8px", padding: "6px", fontSize: "0.6rem", fontWeight: "900", cursor: "pointer" }}
            >
              JETZT HOCHLADEN
            </button>
          </div>
        )}

        {/* EDIT PROFILE Button */}
        {isEditing ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginTop: "20px" }}>
            <input className="admin-input" value={profile.name} onChange={e => setProfile({...profile, name: e.target.value})} placeholder="Künstlername" />
            <input className="admin-input" value={profile.vision} onChange={e => setProfile({...profile, vision: e.target.value.slice(0, 280)})} placeholder="Kurzes Statement (Max 280)" />
            <textarea className="admin-input" value={profile.bio} onChange={e => setProfile({...profile, bio: e.target.value})} placeholder="Deine Bio / Geschichte..." rows={5} />
            <button onClick={handleSave} disabled={loading} style={{ width: "100%", padding: "12px", background: "#00acc1", color: "#000", border: "none", borderRadius: "10px", fontWeight: "900" }}>
              {loading ? "SAVING..." : "SAVE PROFILE"}
            </button>
          </div>
        ) : (
          <div>
            <h2 style={{ margin: "10px 0 5px 0", fontSize: "1.4rem", fontWeight: "900" }}>{profile.name || "STREETWISE ARTIST"}</h2>
            <p style={{ color: "#00acc1", fontSize: "0.65rem", letterSpacing: "2px", fontWeight: "900", marginBottom: "15px" }}>{isAdmin ? "ADMINISTRATOR" : isArtist ? "ARTIST" : "LISTENER"}</p>
            <p style={{ fontSize: "0.9rem", color: "#eee", fontStyle: "italic", marginBottom: "10px" }}>"{profile.vision}"</p>
            <p style={{ fontSize: "0.75rem", color: "#888", lineHeight: "1.5", marginBottom: "20px", whiteSpace: "pre-wrap" }}>{profile.bio}</p>
            <button onClick={() => setIsEditing(true)} style={{ background: "none", border: "1px solid #333", color: "#888", padding: "6px 20px", borderRadius: "20px", fontSize: "0.65rem", cursor: "pointer" }}>EDIT PROFILE</button>
          </div>
        )}
      </div>

      {/* ✅ MY TRACKS PERFORMANCE – nur für Artists mit Tracks */}
      {(isArtist || isAdmin) && myTracks.length > 0 && (
        <div style={{ margin: "20px", padding: "20px", background: "rgba(0,172,193,0.03)", border: "1px solid #1a2a2a", borderRadius: "16px" }}>
          
          <h4 style={{ color: "#00acc1", fontSize: "0.8rem", letterSpacing: "2px", marginBottom: "15px", fontWeight: "900" }}>
            MY TRACKS PERFORMANCE
          </h4>

          {/* TOTAL STATS */}
          <div style={{ display: "flex", gap: "8px", marginBottom: "20px", justifyContent: "center" }}>
            {[
              { icon: "▶", val: totalPlays, label: "PLAYS" },
              { icon: "♥", val: totalLikes, label: "LIKES" },
              { icon: "👎", val: totalDislikes, label: "DISLIKES" },
              { icon: "⏭", val: totalSkips, label: "SKIPS" },
            ].map(s => (
              <div key={s.label} style={{ flex: 1, textAlign: "center", padding: "10px 5px", background: "rgba(255,255,255,0.03)", borderRadius: "10px", border: "1px solid #111" }}>
                <div style={{ fontSize: "1rem", marginBottom: "4px" }}>{s.icon}</div>
                <div style={{ fontSize: "1rem", fontWeight: "900", color: "#fff" }}>{s.val}</div>
                <div style={{ fontSize: "0.5rem", color: "#444", letterSpacing: "1px" }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* ✅ NEU: STUDIO CHECK */}
          <button
            onClick={() => {
              if (!isStudioCheckOpen) generateStudioCheck();
              setIsStudioCheckOpen(!isStudioCheckOpen);
            }}
            style={{ width: "100%", padding: "8px 10px", background: "none", border: "none", borderTop: "1px solid #111", color: "#ff9800", fontSize: "0.8rem", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}
          >
            <span>🔥 STUDIO CHECK – HOT TAKES</span>
            <span>{isStudioCheckOpen ? "▲" : "▼"}</span>
          </button>

          {isStudioCheckOpen && (
            <div style={{ marginTop: "10px", padding: "12px", background: "rgba(255,152,0,0.04)", borderRadius: "10px", border: "1px solid rgba(255,152,0,0.1)" }}>
              {studioCheckLoading ? (
                <div style={{ textAlign: "center", padding: "20px" }}>
                  <div style={{ fontSize: "0.65rem", color: "#ff9800", letterSpacing: "2px" }}>ANALYZING...</div>
                </div>
              ) : studioCheckText ? (
                <div style={{ fontSize: "0.75rem", color: "#ccc", lineHeight: "1.7", whiteSpace: "pre-wrap" }}>
                  {studioCheckText}
                </div>
              ) : (
                <div style={{ fontSize: "0.65rem", color: "#444", textAlign: "center" }}>Klick zum Laden...</div>
              )}
            </div>
          )}

          {/* ✅ PER TRACK – aufklappbar */}
          <button
            onClick={() => setIsStatsOpen(!isStatsOpen)}
            style={{ width: "100%", padding: "8px 10px", background: "none", border: "none", borderTop: "1px solid #111", color: "#00acc1", fontSize: "0.8rem", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "4px" }}
          >
            <span>TRACKS DETAIL ({myTracks.length})</span>
            <span>{isStatsOpen ? "▲" : "▼"}</span>
          </button>

          {isStatsOpen && (
            <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginTop: "10px" }}>
              {myTracks
                .sort((a, b) => (b.plays || 0) - (a.plays || 0))
                .map(t => (
                  <div key={t.id} style={{ display: "flex", alignItems: "center", padding: "8px 10px", background: "rgba(255,255,255,0.02)", borderRadius: "8px", border: "1px solid #111" }}>
                    <div style={{ flex: 1, fontSize: "0.7rem", color: t.onAir ? "#fff" : "#444", fontWeight: "bold", textAlign: "left" }}>
                      {t.onAir ? "📡" : "○"} {t.title}
                    </div>
                    <div style={{ display: "flex", gap: "10px", fontSize: "0.6rem" }}>
                      <span style={{ color: "#666" }}>▶ {t.plays || 0}</span>
                      <span style={{ color: "#00acc1" }}>♥ {t.likes?.length || 0}</span>
                      <span style={{ color: "#ff1744" }}>👎 {t.dislikes?.length || 0}</span>
                      <span style={{ color: "#888" }}>⏭ {t.skipCount || 0}</span>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}

      {/* RESUMEE-DASHBOARD */}
      {(isAdmin || isArtist) && (
        <ResumeDashboard
          tracks={tracks}
          allUsers={allUsers}
          showToast={showToast}
          onSendWeeklyReport={handleSendWeeklyReport}
          sendingReport={sendingWeeklyReport}
        />
      )}

      {/* ✅ NEU: Message an Admin */}
      {!isAdmin && adminUser && (
        <div style={{ marginTop: "15px", textAlign: "center" }}>
          <button 
            onClick={() => setShowMessageComposer(true)}
            style={{ background: "none", border: "none", color: "#444", fontSize: "2.4rem", cursor: "pointer", display: "block", margin: "0 auto"  }}
          >
            💬 
          </button>
          <span style={{ color: "#00acc1", fontSize: "0.9rem", fontWeight: "900", letterSpacing: "1px", display: "block", marginTop: "6px", cursor: "pointer" }}
            onClick={() => setShowMessageComposer(true)}
          >
            NACHRICHT AN ADMIN
          </span>
          <p style={{ fontSize: "0.7rem", color: "#444", margin: "4px 0 0" }}>
            Für Fragen & Feedback zur App
          </p>
        </div>
      )}

      {notifications.length > 0 && (
        <div style={{ margin: "20px", padding: "20px", background: "rgba(0,172,193,0.05)", border: "1px solid #1a3a3a", borderRadius: "16px" }}>
          <h4 style={{ color: "#00acc1", fontSize: "0.6rem", letterSpacing: "2px", marginBottom: "15px", fontWeight: "900" }}>
            📬 NACHRICHTEN ({notifications.filter(n => !n.read).length} neu)
          </h4>
          {notifications.map(n => (
            <div key={n.id} style={{ padding: "12px", background: n.read ? "rgba(255,255,255,0.02)" : "rgba(0,172,193,0.08)", borderRadius: "10px", marginBottom: "8px", border: n.read ? "1px solid #111" : "1px solid #00acc1" }}>
              
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div style={{ fontSize: "0.6rem", color: "#00acc1", marginBottom: "4px", fontWeight: "900" }}>{n.fromName || "Admin"}</div>
                {/* 🗑 Löschen Button */}
                <button
                  onClick={async () => {
                    await deleteDoc(doc(db, "notifications", user.uid, "messages", n.id));
                  }}
                  style={{ background: "none", border: "none", color: "#333", cursor: "pointer", fontSize: "0.7rem", padding: "0" }}
                >
                  🗑
                </button>
              </div>

              <div 
                style={{ fontSize: "0.75rem", color: "#ccc", cursor: "pointer" }}
                onClick={async () => {
                  await updateDoc(doc(db, "notifications", user.uid, "messages", n.id), { read: true });
                }}
              >
                {n.text}
              </div>
              <div style={{ fontSize: "0.5rem", color: "#444", marginTop: "6px" }}>
                {n.createdAt ? new Date(n.createdAt.seconds * 1000).toLocaleString() : ""}
                {!n.read && <span style={{ color: "#00acc1", marginLeft: "8px" }}>● NEU</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ADMIN / ARTIST SECTION */}
      {(isAdmin || isArtist) && (
        <section style={{ borderTop: "1px solid #222", paddingTop: "30px", marginTop: "40px", paddingLeft: "20px", paddingRight: "20px" }}>
          
          <button onClick={shareInvite} style={{ width: "100%", padding: "14px", background: "rgba(0, 172, 193, 0.1)", border: "1px solid #00acc1", color: "#00acc1", borderRadius: "12px", fontWeight: "900", fontSize: "0.7rem", cursor: "pointer", marginBottom: "30px" }}>
            + INVITE NEW ARTIST
          </button>

          {/* PENDING USERS */}
          {isAdmin && pendingUsers.length > 0 && (
            <div style={{ textAlign: "left", marginBottom: "40px" }}>
              <h4 style={{ color: "#ff1744", fontSize: "0.6rem", letterSpacing: "2px", marginBottom: "15px", fontWeight: "900" }}>
                PENDING APPLICATIONS ({pendingUsers.length})
              </h4>
              {pendingUsers.map(p => (
                <div key={p.id} style={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: "15px", padding: "15px", background: "rgba(255, 255, 255, 0.05)", borderRadius: "12px", border: "1px solid #222" }}>
                  <div style={{ fontSize: "0.75rem" }}>
                    <div style={{ fontWeight: "bold", color: "#00acc1" }}>{p.name}</div>
                    <div style={{ color: "#666", fontSize: "0.65rem" }}>{p.email}</div>
                  </div>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button onClick={() => approveUser(p.id)} style={{ flex: 1, background: "#00acc1", border: "none", color: "black", padding: "8px", borderRadius: "6px", fontWeight: "900", fontSize: "0.6rem" }}>APPROVE</button>
                    <button onClick={() => rejectUser(p.id)} style={{ flex: 1, background: "none", border: "1px solid #ff9800", color: "#ff9800", padding: "8px", borderRadius: "6px", fontWeight: "900", fontSize: "0.6rem" }}>DECLINE</button>
                    <button onClick={() => deleteUser(p.id)} style={{ background: "none", border: "1px solid #ff1744", color: "#ff1744", padding: "8px 12px", borderRadius: "6px", fontWeight: "900", fontSize: "0.6rem" }}>DELETE</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ALL USERS */}
          {isAdmin && allUsers && allUsers.length > 0 && (
            <div style={{ textAlign: "left", marginTop: "10px" }}>
              <div
                onClick={() => setIsUserListExpanded(!isUserListExpanded)}
                style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "15px", background: "rgba(255,255,255,0.03)", borderRadius: "12px", cursor: "pointer", border: "1px solid #222", marginBottom: "10px" }}
              >
                <h4 style={{ color: "#888", fontSize: "0.6rem", letterSpacing: "2px", margin: 0, fontWeight: "900" }}>ALL REGISTERED USERS ({allUsers.length})</h4>
                <span style={{ color: "#00acc1", fontSize: "0.8rem" }}>{isUserListExpanded ? "− COLLAPSE" : "+ EXPAND"}</span>
              </div>

              {isUserListExpanded && (
                <div style={{ padding: "15px", background: "rgba(0,0,0,0.2)", borderRadius: "12px", border: "1px solid #111", marginBottom: "30px" }}>
                  <input
                    type="text"
                    placeholder="Search by name or email..."
                    value={userSearchTerm}
                    onChange={(e) => setUserSearchTerm(e.target.value)}
                    style={{ width: "100%", padding: "10px", background: "rgba(255,255,255,0.05)", border: "1px solid #222", borderRadius: "8px", color: "#fff", fontSize: "0.75rem", marginBottom: "15px", outline: "none" }}
                  />

                  <div style={{ display: "flex", flexDirection: "column", gap: "8px", maxHeight: "400px", overflowY: "auto" }}>
                    {allUsers
                      .filter(u => (u.name || "").toLowerCase().includes(userSearchTerm.toLowerCase()) || (u.email || "").toLowerCase().includes(userSearchTerm.toLowerCase()))
                      .sort((a, b) => {
                        const roles = { admin: 1, artist: 2, selector: 3, pending: 4 };
                        const roleA = a.isAdmin ? 1 : (roles[a.role] || 5);
                        const roleB = b.isAdmin ? 1 : (roles[b.role] || 5);
                        if (roleA !== roleB) return roleA - roleB;
                        return (a.name || "").localeCompare(b.name || "");
                      })
                      .map(u => {
                        const isMe = u.id === user.uid;
                        const currentRole = u.isAdmin ? "admin" : (u.role || "selector");
                        return (
                          <div key={u.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px", background: isMe ? "rgba(255, 215, 0, 0.05)" : "rgba(255, 255, 255, 0.02)", borderRadius: "8px", border: isMe ? "1px solid #ffd700" : "1px solid #111" }}>

                            {/* LINKS: Avatar + Infos */}
                            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                              <div style={{ width: "24px", height: "24px", borderRadius: "50%", background: u.avatar ? `url(${u.avatar}) center/cover` : "#000", border: isMe ? "1px solid #ffd700" : "1px solid #333", flexShrink: 0 }} />
                              <div>
                                <div style={{ fontSize: "0.7rem", fontWeight: "bold", color: isMe ? "#ffd700" : "#eee" }}>{u.name || "Anonymous"}</div>
                                <div style={{ fontSize: "0.55rem", color: "#555" }}>{u.email}</div>
                                <div style={{ fontSize: "0.5rem", color: "#444", marginTop: "2px" }}>
                                  Joined: {u.createdAt ? new Date(u.createdAt.seconds * 1000).toLocaleDateString() : "—"}
                                  {" · "}
                                  Last: {u.lastLogin ? new Date(u.lastLogin.seconds * 1000).toLocaleDateString() : "—"}
                                </div>
                              </div>
                            </div>

                            {/* RECHTS: Message + Role */}
                            <div style={{ display: "flex", gap: "6px", alignItems: "center", flexShrink: 0 }}>
                              {!isMe && (
                                <button
                                  onClick={() => setMessageTarget(u)}
                                  style={{ background: "none", border: "1px solid #333", color: "#666", padding: "4px 8px", borderRadius: "6px", fontSize: "0.55rem", cursor: "pointer" }}
                                >
                                  ✉️
                                </button>
                              )}

                              {/* ✅ NEU: djMode Toggle */}
                              <button
                                onClick={() => updateUserRole(u.id, u.role, !u.djMode)}
                                style={{ 
                                  background: "none", 
                                  border: `1px solid ${u.djMode ? "#00acc1" : "#333"}`, 
                                  color: u.djMode ? "#00acc1" : "#444", 
                                  padding: "4px 8px", borderRadius: "6px", 
                                  fontSize: "0.55rem", cursor: "pointer",
                                  fontWeight: "900"
                                }}
                              >
                                🎛
                              </button>

                              {/* 🚀 NEU: SEND WEEKLY RESUMÉ BUTTON */}
                              {(u.role === "artist" || u.isAdmin) && (
                                <button
                                  onClick={() => handleSendWeeklyReport(u)}
                                  disabled={sendingWeeklyReport}
                                  style={{ 
                                    background: "rgba(0,172,193,0.1)", 
                                    border: "1px solid #00acc1", 
                                    color: "#00acc1", 
                                    padding: "4px 8px", 
                                    borderRadius: "6px", 
                                    fontSize: "0.55rem", 
                                    cursor: "pointer",
                                    fontWeight: "900",
                                    opacity: sendingWeeklyReport ? 0.5 : 1
                                  }}
                                >
                                  {sendingWeeklyReport ? "..." : "RESUMÉ"}
                                </button>
                              )}

                              <select
                                value={currentRole}
                                onChange={(e) => updateUserRole(u.id, e.target.value)}
                                style={{ background: "#000", color: currentRole === "artist" ? "#00acc1" : currentRole === "admin" ? "#ff1744" : "#888", border: "1px solid #222", fontSize: "0.55rem", padding: "4px 8px", borderRadius: "6px", fontWeight: "900", cursor: "pointer" }}
                              >
                                <option value="selector">SELECTOR</option>
                                <option value="artist">ARTIST</option>
                                <option value="admin">ADMINISTRATOR</option>
                                <option value="pending">PENDING</option>
                              </select>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {/* MESSAGE MODAL */}
      {messageTarget && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.9)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 5000, padding: "20px", boxSizing: "border-box" }}>
          <div style={{ background: "#1a1f2b", padding: "25px", borderRadius: "20px", width: "100%", maxWidth: "400px", border: "1px solid #00acc1" }}>
            <h3 style={{ color: "#00acc1", fontSize: "0.9rem", marginTop: 0 }}>
              MESSAGE AN {messageTarget.name?.toUpperCase()}
            </h3>
            <textarea
              value={messageText}
              onChange={e => setMessageText(e.target.value)}
              placeholder="Deine Nachricht..."
              rows={4}
              style={{ width: "100%", background: "#000", border: "1px solid #333", borderRadius: "10px", color: "#fff", padding: "10px", fontSize: "0.75rem", marginBottom: "15px", boxSizing: "border-box", resize: "none" }}
            />
            <button
              onClick={handleSendMessage}
              style={{ width: "100%", padding: "12px", background: "#00acc1", color: "#000", border: "none", borderRadius: "10px", fontWeight: "900", cursor: "pointer", marginBottom: "10px" }}
            >
              SENDEN
            </button>
            <button
              onClick={() => { setMessageTarget(null); setMessageText(""); }}
              style={{ width: "100%", background: "none", border: "none", color: "#666", fontSize: "0.7rem", cursor: "pointer" }}
            >
              ABBRECHEN
            </button>
          </div>
        </div>
      )}

      {showMessageComposer && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.9)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 5000, padding: "20px", boxSizing: "border-box" }}>
          <div style={{ background: "#1a1f2b", padding: "25px", borderRadius: "20px", width: "100%", maxWidth: "400px", border: "1px solid #00acc1" }}>
            <h3 style={{ color: "#00acc1", fontSize: "0.9rem", marginTop: 0 }}>💬 NACHRICHT AN ADMIN</h3>
            <p style={{ fontSize: "0.65rem", color: "#666", marginBottom: "15px" }}>Für Fragen & Feedback zur App.</p>
            <textarea
              value={composerText}
              onChange={e => setComposerText(e.target.value)}
              placeholder="Deine Nachricht..."
              rows={4}
              style={{ width: "100%", background: "#000", border: "1px solid #333", borderRadius: "10px", color: "#fff", padding: "10px", fontSize: "0.75rem", marginBottom: "15px", boxSizing: "border-box", resize: "none" }}
            />
            <button onClick={handleSendToAdmin} style={{ width: "100%", padding: "12px", background: "#00acc1", color: "#000", border: "none", borderRadius: "10px", fontWeight: "900", cursor: "pointer", marginBottom: "10px" }}>
              SENDEN
            </button>
            <button onClick={() => { setShowMessageComposer(false); setComposerText(""); }} style={{ width: "100%", background: "none", border: "none", color: "#666", fontSize: "0.7rem", cursor: "pointer" }}>
              ABBRECHEN
            </button>
          </div>
        </div>
      )}

      {/* LOGOUT */}
      <button onClick={() => { signOut(auth); window.location.reload(); }} style={{ display: "block", margin: "50px auto", background: "none", border: "none", color: "#444", fontSize: "0.6rem", fontWeight: "bold", cursor: "pointer" }}>
        LOGOUT
      </button>
    </div>
  );
}